<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useFloating, offset, shift, flip, autoUpdate } from "@floating-ui/vue";
import { ChevronDown } from "lucide-vue-next";

// Utils
import { useI18n } from "vue-i18n";
import { BigNumber } from "ethers";
import { roundToDecimals } from "~/utils/math";
import useConvert from "~/composables/useConvert";
import { formatDate } from "~/utils/dates";

// Api
import { auctionApi } from "~/api/auction";

// Stores
import { useStoreMarketplace } from "~/stores/storeMarketplace";
import usePOLPrice from "~/stores/POLprice";

// Payments
import { Currency, MarketSortType } from "fungi-types";

// Types
import { MarketType, NftEquipment, NftSkill } from "fungi-types";
import { SalesHistoryResponse, PaymentMethod } from "~/api/auction/types";

const props = withDefaults(
  defineProps<{
    marketType: MarketType;
    count: number;
    currencySymbol: string;
    paymentMethod: PaymentMethod;
    currency: Currency;
    card: NftSkill | NftEquipment;
  }>(),
  {
    count: 0,
  }
);

const { t } = useI18n();
const storeMarketplace = useStoreMarketplace();

const { weiToMatic, getAuctionAmount } = useConvert;
const polPrice = usePOLPrice();

//// MODAL BID NEW

const handleResize = () => {
  if (isHovered.value) {
    update();
  }
};

onMounted(() => {
  // Add resize event listener
  window.addEventListener("resize", handleResize);

  // Alternatively, use autoUpdate for automatic positioning
  // This handles resize, scroll, and other layout changes
  const cleanup = autoUpdate(triggerList.value, dropdown.value, update, {
    animationFrame: true, // Use requestAnimationFrame for smoother updates
  });

  // Store cleanup function to call on unmount
  onBeforeUnmount(cleanup);
});

// Or if not using autoUpdate, clean up manual resize listener
onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});

const sales = ref<SalesHistoryResponse["result"]>([]);
const loading = ref(false);

const fetchSales = async () => {
  loading.value = true;
  const list = await auctionApi.sales.history.list.query({
    scarcities: [(props.card as NftSkill).scarcity],
    skills: [(props.card as NftSkill).skill],
    tennisPlayerIds: [(props.card as NftSkill).tennisPlayerId],
    seasons: [Number((props.card as NftSkill).season)],
    sort: MarketSortType.ENDING_DESC,
    limit: 10,
  });
  console.log("list", list);
  sales.value = list.result;
  loading.value = false;
};

const salesList = computed(() => {
  if (loading.value) return [];
  return sales.value.map((bid) => {
    let amount = getAuctionAmount({
      scarcity: (bid as NftSkill).scarcity,
      marketType:
        (bid as NftSkill).auction?.marketType === MarketType.SECONDARY
          ? MarketType.SECONDARY
          : MarketType.PRIMARY,
      usdcAmount: (bid as NftSkill).auction?.lastBidAmountUsdc || "0000",
      weiAmount:
        (bid as NftSkill).auction?.lastBidAmount || (bid as NftSkill).auction?.startedAmount || "0",
    }).fiat.usd;
    return {
      ...bid,
      amount,
      date: formatDate(bid.auction?.endedAt || new Date(), "dd MMM h:m:s"),
    };
  });
});

// Reference to the trigger element (what gets hovered)
const triggerList = ref(null);
// Reference to the tooltip/content that appears on hover
const dropdown = ref(null);
// Track hover state
const isHovered = ref(false);
// Track if the user is on mobile
const isMobile = ref(false);

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768;
};

const { floatingStyles, update } = useFloating(triggerList, dropdown, {
  placement: "bottom-start",
  middleware: [offset(0), shift(), flip()],
});

// Event handlers for mouse interactions
const handleMouseEnter = () => {
  checkMobile();
  isHovered.value = true;
};

const handleMouseLeave = () => {
  isHovered.value = false;
};

onMounted(() => fetchSales());
</script>

<template>
  <div
    @mouseover="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    class="w-full md:w-fit justify-between rounded-full border-neutral-900 border px-3 p-1 text-xs flex flex-row items-center gap-2 pr-3 cursor-pointer"
    :class="[isHovered && ' bg-neutral-900', salesList.length === 0 && 'hidden']"
    ref="triggerList"
  >
    <div class="text-nowrap whitespace-nowrap flex gap-2">
      <div>
        {{
          t("_shared.recentSale", {
            count: salesList.length,
          })
        }}
      </div>
    </div>
    <ChevronDown class="h-4 w-4" />
    <div
      ref="dropdown"
      :style="[floatingStyles, { visibility: isHovered ? 'visible' : 'hidden' }]"
      class="bg-neutral-200 rounded-md z-50 flex flex-col gap-1 text-neutral-900 border border-white/10 max-h-72"
      :class="[isMobile && '!fixed !top-auto !translate-x-0 max-h-60 !bottom-0 !left-0 !right-0']"
    >
      <div class="flex flex-col divide-y divide-neutral-300 h-full overflow-auto">
        <div
          v-for="sale in salesList"
          :key="sale.auction?.cuid"
          class="flex min-w-64 text-xs justify-between items-center p-3"
        >
          <div>
            <div class="text-black">{{ sale.auction?.lastBidder?.username }}</div>
            <div class="text-neutral-500">{{ sale.date }}</div>
          </div>
          <div class="text-sm font-medium flex flex-row whitespace-nowrap items-center gap-1">
            {{ sale.amount }} $
          </div>
        </div>
      </div>
      <div
        @click="handleMouseLeave"
        class="text-center p-3 border-t font-semibold border-neutral-400 active:bg-neutral-300 cursor-pointer"
        :class="[!isMobile && 'hidden']"
      >
        {{ t("_shared.close") }}
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
